import { ColDef, GetRowIdFunc, GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-alpine.css'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import CancelIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { Search } from 'genesis-suite/icons'
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { PAGE_SIZE } from '../manage-users/formTable/ManageUsers'

LicenseManager.setLicenseKey(
    'Using_this_{AG_Grid}_Enterprise_key_{AG-070843}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{CGN_AND_ASSCOCIATES_INC}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{4_January_2026}____[v3]_[01]_MTc2NzQ4NDgwMDAwMA==1e2a1f0bfc91a65f122949b8c5818174'
)

const escapeRegExp = (value: string) => {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}

interface AgDataTableProps extends Partial<GridOptions> {
    columns: any[]
    handleCellEdit?: (newRow: any) => void
    onGridReady?: (params: GridReadyEvent) => void
    onRowSelected?: (event: any) => void
    extraActions: React.JSX.Element[]
    title: string
    handleSearch: (searchText: string) => void
    gridStatus: string
    getRowId: GetRowIdFunc
    rowSelection?: 'single' | 'multiple'
}

const AgDataTable = forwardRef(
    (
        {
            columns,
            handleCellEdit,
            onGridReady,
            onRowSelected,
            extraActions,
            title,
            handleSearch,
            gridStatus,
            getRowId,
            rowSelection = 'multiple',
        }: AgDataTableProps,
        ref
    ) => {
        const gridRef = useRef<AgGridReact<any>>(null)

        const [searchBarText, setSearchBarText] = useState('')

        const onSearch = useDebouncedCallback((searchText: string) => {
            handleSearch(searchText)
        }, 500)

        const defaultColDef = useMemo<ColDef>(() => {
            return {
                editable: false,
                resizable: true,
                cellClass: 'tada-cell',
                wrapHeaderText: true,
                autoHeaderHeight: true,
                lockPinned: true,
                sortable: true,
                minWidth: 100,
                filter: 'agTextColumnFilter',
                filterParams: {
                    maxNumConditions: 1,
                    filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'],
                },
            }
        }, [])

        const updateUser = user => {
            gridRef.current?.api.forEachNode(rowNode => {
                if (rowNode.data.userId === user.userId) {
                    const updated = { ...rowNode.data, ...user }
                    rowNode.updateData(updated)
                }
            })
        }

        useImperativeHandle(ref, () => ({
            getUsers,
            updateUser,
        }))

        const getUsers = () => {
            const users = []
            gridRef.current?.api.forEachNode(node => users.push(node))
            return users
        }

        const gridOptions: GridOptions = {
            columnDefs: columns,
            alwaysMultiSort: false,
            suppressRowDeselection: true,
            multiSortKey: 'ctrl',
            suppressPaginationPanel: true,
            suppressRowTransform: true,
            onGridReady: onGridReady,
            onRowSelected: onRowSelected,
            defaultColDef: defaultColDef,
            rowSelection: rowSelection == 'single' ? 'single' : 'multiple',
            rowModelType: 'serverSide',
            onCellValueChanged: handleCellEdit,
            suppressPropertyNamesCheck: true,
            stopEditingWhenCellsLoseFocus: true,
            pagination: false,
            paginationPageSize: PAGE_SIZE,
            cacheBlockSize: PAGE_SIZE,
            getRowId,
        }
        const defaultModules = [MenuModule, ColumnsToolPanelModule]
        const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), [])

        return (
            <>
                <Box sx={{ display: 'flex', flexFlow: 'column', flex: 1, height: '100%' }}>
                    <Box
                        sx={{
                            mb: 1,
                            flex: 0,
                            minHeight: 50,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexFlow: {
                                sm: 'column nowrap',
                                md: 'row nowrap',
                            },
                        }}
                    >
                        <Typography variant="h5">{title}</Typography>

                        <Box sx={{ marginLeft: 1, flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <TextField
                                sx={{
                                    minWidth: 286,
                                    '& .MuiInputBase-root': { padding: '0px 0px 0px 8px', position: 'relative' },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 28 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchBarText && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    setSearchBarText('')
                                                    handleSearch('')
                                                }}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                label="Search"
                                size="small"
                                variant="outlined"
                                value={searchBarText}
                                onChange={e => {
                                    setSearchBarText(e.target.value)
                                    onSearch(e.target.value)
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flex: 0, alignItems: 'center' }}>
                            {extraActions && extraActions.map(action => action)}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flex: 0, justifyContent: 'flex-end' }}>
                        <Typography>{gridStatus}</Typography>
                    </Box>
                    <Box
                        style={gridStyle}
                        sx={theme_sx => ({
                            width: '100%',
                            '& .invalid-cell': { backgroundColor: 'red' },
                        })}
                    >
                        <AgGridReact
                            className="ag-theme-alpine"
                            ref={gridRef}
                            modules={[...defaultModules, ServerSideRowModelModule, SetFilterModule]}
                            {...gridOptions}
                            noRowsOverlayComponent={NoRowsOverlay}
                            loadingOverlayComponent={overlayLoadingTemplate}
                        />
                    </Box>
                </Box>
            </>
        )
    }
)
const NoRowsOverlay = () => {
    return (
        <Box height="100%" p={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <Box m={1}>
                <Search />
            </Box>
            <Typography>No results found meeting selected criteria</Typography>
        </Box>
    )
}
const overlayLoadingTemplate = () => {
    const {
        palette: { primary },
    } = useTheme()
    return (
        <Box
            sx={{
                textAlign: 'center',
                position: 'relative',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            }}
        >
            <CircularProgress sx={{ margin: 'auto' }} color="primary" />
        </Box>
    )
}

export default AgDataTable
